import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';

const MappingConnectionsOverlay = ({
  connections = [],
  mediaLibrary = [],
  courseStructure = [],
  onConnectionClick
}) => {
  const [connectionLines, setConnectionLines] = useState([]);

  useEffect(() => {
    // Calculate connection line positions
    const updateConnectionLines = () => {
      const lines = connections?.map(connection => {
        const sourceElement = document.querySelector(`[data-testid="source-media-${connection?.sourceId}"]`);
        const targetElement = document.querySelector(`[data-testid="target-lesson-${connection?.targetId}"]`);
        
        if (!sourceElement || !targetElement) return null;

        const sourceRect = sourceElement?.getBoundingClientRect();
        const targetRect = targetElement?.getBoundingClientRect();
        const containerRect = document.querySelector('.grid.grid-cols-1.lg\\:grid-cols-2')?.getBoundingClientRect();
        
        if (!containerRect) return null;

        return {
          ...connection,
          x1: sourceRect?.right - containerRect?.left,
          y1: sourceRect?.top + sourceRect?.height / 2 - containerRect?.top,
          x2: targetRect?.left - containerRect?.left,
          y2: targetRect?.top + targetRect?.height / 2 - containerRect?.top,
          sourceTitle: mediaLibrary?.find(item => item?.id === connection?.sourceId)?.title,
          targetTitle: courseStructure
            ?.flatMap(course => course?.modules)
            ?.flatMap(module => module?.lessons)
            ?.find(lesson => lesson?.id === connection?.targetId)?.title
        };
      })?.filter(Boolean);

      setConnectionLines(lines);
    };

    updateConnectionLines();

    // Update on scroll and resize
    const handleUpdate = () => {
      requestAnimationFrame(updateConnectionLines);
    };

    window.addEventListener('scroll', handleUpdate);
    window.addEventListener('resize', handleUpdate);

    // Update every second for dynamic changes
    const interval = setInterval(updateConnectionLines, 1000);

    return () => {
      window.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
      clearInterval(interval);
    };
  }, [connections, mediaLibrary, courseStructure]);

  const getConnectionColor = (status) => {
    switch (status) {
      case 'active': return '#10b981'; // success color
      case 'conflict': return '#ef4444'; // error color
      case 'pending': return '#f59e0b'; // warning color
      default: return '#6b7280'; // muted color
    }
  };

  const getConnectionStroke = (status) => {
    switch (status) {
      case 'conflict': return '4,4'; // dashed line
      case 'pending': return '2,2'; // dotted line
      default: return 'none'; // solid line
    }
  };

  if (connectionLines?.length === 0) return null;

  return (
    <div className="absolute inset-0 pointer-events-none z-10">
      <svg className="w-full h-full">
        <defs>
          {/* Arrow markers for different connection states */}
          <marker
            id="arrowhead-active"
            markerWidth="10"
            markerHeight="7"
            refX="10"
            refY="3.5"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill={getConnectionColor('active')}
            />
          </marker>
          <marker
            id="arrowhead-conflict"
            markerWidth="10"
            markerHeight="7"
            refX="10"
            refY="3.5"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill={getConnectionColor('conflict')}
            />
          </marker>
          <marker
            id="arrowhead-pending"
            markerWidth="10"
            markerHeight="7"
            refX="10"
            refY="3.5"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill={getConnectionColor('pending')}
            />
          </marker>
        </defs>

        {connectionLines?.map((line) => (
          <g key={line?.connectionId}>
            {/* Connection Line */}
            <line
              x1={line?.x1}
              y1={line?.y1}
              x2={line?.x2}
              y2={line?.y2}
              stroke={getConnectionColor(line?.status)}
              strokeWidth="2"
              strokeDasharray={getConnectionStroke(line?.status)}
              markerEnd={`url(#arrowhead-${line?.status})`}
              className="cursor-pointer pointer-events-auto hover:stroke-[3px] transition-all"
              onClick={() => onConnectionClick?.(line)}
            />

            {/* Connection Status Indicator */}
            <circle
              cx={(line?.x1 + line?.x2) / 2}
              cy={(line?.y1 + line?.y2) / 2}
              r="8"
              fill={getConnectionColor(line?.status)}
              className="cursor-pointer pointer-events-auto hover:r-10 transition-all"
              onClick={() => onConnectionClick?.(line)}
            />

            {/* Status Icon */}
            <foreignObject
              x={(line?.x1 + line?.x2) / 2 - 6}
              y={(line?.y1 + line?.y2) / 2 - 6}
              width="12"
              height="12"
              className="pointer-events-none"
            >
              <div className="w-full h-full flex items-center justify-center">
                <Icon
                  name={
                    line?.status === 'active' ? 'Check' :
                    line?.status === 'conflict' ? 'X' :
                    line?.status === 'pending' ? 'Clock' : 'Link'
                  }
                  size={8}
                  color="white"
                />
              </div>
            </foreignObject>
          </g>
        ))}
      </svg>

      {/* Connection Legend */}
      {connectionLines?.length > 0 && (
        <div className="absolute top-4 right-4 bg-card/95 backdrop-blur-sm border border-border rounded-lg p-4 shadow-tier-2 pointer-events-auto">
          <h4 className="text-sm font-medium text-foreground mb-3">Conexões</h4>
          <div className="space-y-2">
            <div className="flex items-center space-x-2 text-xs">
              <div className="w-4 h-0.5 bg-success"></div>
              <Icon name="Check" size={12} color="var(--color-success)" />
              <span className="text-muted-foreground">Ativa</span>
            </div>
            <div className="flex items-center space-x-2 text-xs">
              <div className="w-4 h-0.5 bg-error" style={{ backgroundImage: 'repeating-linear-gradient(to right, #ef4444 0, #ef4444 4px, transparent 4px, transparent 8px)' }}></div>
              <Icon name="X" size={12} color="var(--color-error)" />
              <span className="text-muted-foreground">Conflito</span>
            </div>
            <div className="flex items-center space-x-2 text-xs">
              <div className="w-4 h-0.5 bg-warning" style={{ backgroundImage: 'repeating-linear-gradient(to right, #f59e0b 0, #f59e0b 2px, transparent 2px, transparent 4px)' }}></div>
              <Icon name="Clock" size={12} color="var(--color-warning)" />
              <span className="text-muted-foreground">Pendente</span>
            </div>
          </div>

          {/* Connection Count */}
          <div className="mt-3 pt-3 border-t border-border">
            <div className="text-xs text-muted-foreground">
              {connectionLines?.length} conexão{connectionLines?.length !== 1 ? 'ões' : ''} ativa{connectionLines?.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MappingConnectionsOverlay;